import { useCallback, useMemo, useRef } from "react";
import { CategoryTree, CategoryPills } from "src/molecules/CategoryTree";
import {
  CategoryTreeWrapper,
  GridWrapper,
  HeaderSortWrapper,
  ListingContainer,
  MobileCategoryPillsWrapper,
  ProductListContentGrid,
} from "./ProductList.styled";
import { MobileTitleContainer } from "./components/Facets/Facets.styled";
import { AdditionalCategories } from "../AdditionalCategories";
import { CategoryTreeProvider } from "src/contexts/category-tree-context";
import { ListingStoreProvider } from "src/hooks/stores/use-listing-store";
import { Heading } from "@otrium/atoms";
import { HeaderSortSection } from "./HeaderSortSection";
import { Grid } from "./Grid";
import { StickyTitle } from "./components/StickyTitle";
import { NavigationItems } from "src/types/graphql.d";

import { scrollToElement } from "src/utils/scrollToElement";
import { useElementHeight } from "src/hooks/useElementHeight";
import { HEADER_STICKY_OFFSET } from "src/utils/constants";
import type {
  Gender,
  ProductListType,
  SerializedListingResult,
} from "src/types/product-listing";
import { CnstrcListingMarker } from "./components/CnstrListingMarker";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { capitalize } from "src/utils/helper/string";
import { buildPlpHeading } from "src/utils/buildPlpPageTitle";

type ProductListDefaultProps = {
  slug: string;
  categoryId?: string;
  gender?: Gender;
  pageContentType: ProductListType;
  excludeFilters?: ListingFacetKeys[];
  title?: string;
  navigationItems?: NavigationItems | null;
  hitsPerPage?: number;
  collectionId?: string;
  hideCategoryTree?: boolean;
  query?: string;
  /** sameAs links for brand schema enrichment (Wikidata, official site, socials) */
  brandSameAs?: string[];
  initialListingState: SerializedListingResult;
};

const INCLUDE_ADDITIONAL_CATEGORIES: ProductListType[] = ["CATEGORY_PLP"];
const HIDE_STICKY_TITLE_PLP_TYPES: ProductListType[] = ["COLLECTIONS_PLP"];
const HIDE_MOBILE_TITLE_PLP_TYPES: ProductListType[] = [
  "COLLECTIONS_PLP",
  "BRAND_PLP",
];
// On these the category pills carry the mobile title, so the page renders none.
const PILLS_TITLED_PLP_TYPES: ProductListType[] = [
  "CATEGORY_PLP",
  "SHOP_TYPE_PLP",
];
const FORMATTED_TITLE_PLP_TYPES: ProductListType[] = ["SEARCH_PLP"];

const DEFAULT_SELECTED_FACETS: readonly ListingFacetKeys[] = [
  "category",
  "brands",
  "size",
  "price",
  "colors",
  "pattern",
  "style",
  "discount",
] as const;

const SELECTED_FACETS_BY_PLP: Partial<
  Record<ProductListType, readonly ListingFacetKeys[]>
> = {
  BRAND_PLP: [
    "category",
    "size",
    "price",
    "colors",
    "pattern",
    "style",
    "discount",
  ],
} as const;

function ProductList({
  gender,
  slug,
  categoryId,
  pageContentType,
  excludeFilters,
  title,
  navigationItems,
  collectionId,
  query,
  hideCategoryTree = false,
  hitsPerPage = 72,
  brandSameAs,
  initialListingState,
}: ProductListDefaultProps): JSX.Element {
  const { i18n } = useLingui();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [titleRef, titleHeight] = useElementHeight<HTMLDivElement>();

  const handleScrollToTop = useCallback(() => {
    scrollToElement(gridRef.current, HEADER_STICKY_OFFSET, pageContentType);
  }, [pageContentType]);

  const localizedTitle = useMemo(
    () =>
      !FORMATTED_TITLE_PLP_TYPES.includes(pageContentType)
        ? title || (gender && gender !== ShopType.All)
          ? i18n._(capitalize(gender as string))
          : i18n._(msg`All products`)
        : title || "",
    [title, gender, i18n, pageContentType]
  );

  const selectedFacets = useMemo(() => {
    const base =
      SELECTED_FACETS_BY_PLP[pageContentType] || DEFAULT_SELECTED_FACETS;

    if (!excludeFilters?.length) {
      return base;
    }

    return base.filter((facet) => !excludeFilters.includes(facet));
  }, [pageContentType, excludeFilters]);

  const pillsAreTitle =
    !hideCategoryTree && PILLS_TITLED_PLP_TYPES.includes(pageContentType);

  const facetValue =
    pageContentType === "SEARCH_PLP"
      ? query || ""
      : query || collectionId || slug;

  return (
    <ListingStoreProvider initialListingState={initialListingState}>
      <CategoryTreeProvider>
        <CnstrcListingMarker
          pageContentType={pageContentType}
          query={query}
          slug={slug}
          categoryId={categoryId}
        >
          <ListingContainer>
            <ProductListContentGrid>
              {!hideCategoryTree &&
              !HIDE_STICKY_TITLE_PLP_TYPES.includes(pageContentType) ? (
                <StickyTitle
                  containerRef={titleRef}
                  title={buildPlpHeading({
                    i18n,
                    productListType: pageContentType,
                    title: title || localizedTitle,
                    gender,
                  })}
                  formattedTitle={FORMATTED_TITLE_PLP_TYPES.includes(
                    pageContentType
                  )}
                />
              ) : null}

              {!HIDE_MOBILE_TITLE_PLP_TYPES.includes(pageContentType) &&
              !pillsAreTitle ? (
                <MobileTitleContainer>
                  <Heading
                    as="h3"
                    variant="h3"
                    className={
                      FORMATTED_TITLE_PLP_TYPES.includes(pageContentType)
                        ? "formatted-title"
                        : undefined
                    }
                  >
                    {title || localizedTitle}
                  </Heading>
                </MobileTitleContainer>
              ) : null}

              {!hideCategoryTree ? (
                <MobileCategoryPillsWrapper>
                  <CategoryPills
                    gender={gender}
                    categorySlug={slug}
                    pageContentType={pageContentType}
                    isPageTitle={pillsAreTitle}
                    handleScrollToTop={handleScrollToTop}
                  />
                </MobileCategoryPillsWrapper>
              ) : null}

              <HeaderSortWrapper
                className={hideCategoryTree ? "full-width" : ""}
              >
                <HeaderSortSection
                  handleScrollToTop={handleScrollToTop}
                  pageContentType={pageContentType}
                  facetsProps={{
                    pageContentType,
                    gridRef,
                    categorySlug: slug,
                    selectedFacets,
                    formattedTitle:
                      FORMATTED_TITLE_PLP_TYPES.includes(pageContentType),
                    gender,
                    title: title || localizedTitle,
                  }}
                />
              </HeaderSortWrapper>

              {!hideCategoryTree ? (
                <CategoryTreeWrapper>
                  {INCLUDE_ADDITIONAL_CATEGORIES.includes(pageContentType) ? (
                    <AdditionalCategories navigationItems={navigationItems} />
                  ) : null}

                  <CategoryTree
                    gender={gender}
                    categorySlug={slug}
                    pageContentType={pageContentType}
                    handleScrollToTop={handleScrollToTop}
                    stickyTop={HEADER_STICKY_OFFSET + titleHeight}
                  />
                </CategoryTreeWrapper>
              ) : null}

              <GridWrapper
                ref={gridRef}
                className={hideCategoryTree ? "full-width" : ""}
              >
                <Grid
                  title={title}
                  formattedTitle={FORMATTED_TITLE_PLP_TYPES.includes(
                    pageContentType
                  )}
                  gender={gender}
                  pageContentType={pageContentType}
                  slug={slug}
                  categoryId={categoryId}
                  collectionId={collectionId}
                  facetValue={facetValue}
                  hitsPerPage={hitsPerPage}
                  gridRef={gridRef}
                  hideCategoryTree={hideCategoryTree}
                  brandSameAs={brandSameAs}
                />
              </GridWrapper>
            </ProductListContentGrid>
          </ListingContainer>
        </CnstrcListingMarker>
      </CategoryTreeProvider>
    </ListingStoreProvider>
  );
}

export { ProductList };
